import './App.css';
import axios from 'axios';
import { ToastContainer } from 'react-toastify';
import { useContext, useEffect, lazy, Suspense, useState, useCallback } from 'react';
import { Navigate, Outlet, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import AuthContext from './store/AuthContext';
import Layout from 'components/commonLayouts/Layout/Layout';
import Loader from 'components/commonLayouts/Loader/Loader';
import RequestFinancing from './pages/dealFinancing/common/requestFinancing/RequestFinancing';
import ClearNotificationCatOnRouteChange from './pages/notifications/ClearNotificationCat';
import PricingConfigurator from './pages/internalOps/pricingConfigurator/PricingConfigurator';
import { setNavigate } from './components/navigationHelper';
import Header from 'components/commonLayouts/Header/Header';
import OpenKnotsOrNotFound from 'components/openKnots/OpenKnotsOrNotFound';
import OpenKnotsContainer from 'components/openKnots/OpenKnotsContainer';
import ProductAccessGuard from 'components/productAccess/ProductAccessGuard';
import { resolveSafePostLoginPath } from './utils/postLoginRedirect';
import { RealtimeProvider } from './realtime/RealtimeProvider';
import RealtimeStoreBinder from './realtime/RealtimeStoreBinder';
import { recordPathname } from './utils/routeHistory';
import OpenKnotsForgotPassword from 'pages/openKnotsLogin/forgotPassword/OpenKnotsForgotPassword';
import OpenKnotsResetPassword from 'pages/openKnotsLogin/resetPassword/OpenKnotsResetPassword';
import { getLogoutLoginPath, OPENKNOTS_LOGIN_PATH, setLoginOrigin } from 'utils/loginOrigin';
import { buildUnauthenticatedLoginTarget } from './utils/unauthenticatedLogin';
const ForgotPassword = lazy(() => import('./pages/forgotPassword/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/resetPassword/ResetPassword'));
const Login = lazy(() => import('./pages/login/Login'));
const OpenKnotsLogin = lazy(() => import('./pages/openKnotsLogin/OpenKnotsLogin'));
const Dashboard = lazy(() => import('./pages/dashboard/Dashboard'));
const MyProfile = lazy(() => import('./pages/myProfile/MyProfile'));
const CreateRfq = lazy(() => import('./pages/createRfq/CreateRfq'));
const PriceSimulator = lazy(() => import('./pages/priceSimulator/PriceSimulator'));
const Profile = lazy(() => import('./pages/internalOps/manageProfile/CreateProfile'));
const RfqDetails = lazy(() => import('./pages/rfqDetails/RfqDetails'));
const ManageProfile = lazy(() => import('./pages/internalOps/manageProfile/ManageProfile'));
const MyRequests = lazy(() => import('./pages/myRequests/MyRequests'));
const Notifications = lazy(() => import('./pages/notifications/Notifications'));
const AllRfq = lazy(() => import('./pages/internalOps/allRfqs/AllRfq'));
const TransactionDetails = lazy(() => import('./pages/transactionDetails/TransactionDetails'));
const InternalOpsRequestPage = lazy(() => import('./pages/myRequests/internalOps/MyRequests'));
const UserAssignment = lazy(() => import('./pages/userAssignment/UserAssignment'));
const AllTransaction = lazy(() => import('./pages/internalOps/allTransactions/AllTransaction'));
const AllFacilities = lazy(() => import('./pages/facilities/allFacilities/AllFacilities'));
const FacilityDetails = lazy(() => import('./pages/facilities/facilityDetail/FacilityDetails'));
const CreateMasterInvestorFacility = lazy(
    () => import('./pages/facilities/createFacilities/createMasterInvestorFacility/CreateMasterInvestorFacility'),
);
const BuyerFacility = lazy(() => import('./pages/facilities/createFacilities/BuyerFacility'));
const InvestorFacility = lazy(() => import('./pages/facilities/createFacilities/InvestorFacility'));
const InvestmentHistory = lazy(() => import('./pages/investor/investmentHistory'));
const InvestorProfile = lazy(() => import('./pages/myProfile/InvestorProfile'));
const LiveFacilityLine = lazy(() => import('./pages/investor/liveFacilityLine'));
const AuditTrail = lazy(() => import('./pages/investor/liveFacilityLine/auditTrail'));
const ChatEnquiry = lazy(() => import('./pages/chat/supplier/ChatEnquiry'));
const AllBroker = lazy(() => import('./pages/allBroker/AllBroker'));
const SupplierDetails = lazy(() => import('./pages/myProfile/SupplierDetails'));
const OpsTowerDemo = lazy(() => import('./pages/demo/OpsTower'));
const DisputeDemo = lazy(() => import('./pages/demo/Dispute'));
const SupplierDashboardDemo = lazy(() => import('./pages/demo/SupplierDashboard'));
const IAM = lazy(() => import('./pages/internalOps/iam/IAM'));
const TradeSupportLimitPage = lazy(() => import('./pages/tradeSupportLimit'));

function ProtectedLayoutOutlet({ isAuth }) {
    if (isAuth === true) {
        return <Outlet />;
    }
    if (isAuth === undefined) {
        return (
            <div className='mt-5 pt-5'>
                <Loader />
            </div>
        );
    }
    return null;
}

/**
 * After login, send the user to the URL they originally requested (e.g. deep link to Open Knots RFQ).
 */
function PostLoginRedirect({ defaultPath = '/dashboard' }) {
    const location = useLocation();
    const fromState =
        typeof location.state?.from === 'string' ? resolveSafePostLoginPath(location.state.from) : null;
    const redirectParam = resolveSafePostLoginPath(new URLSearchParams(location.search).get('redirect'));
    let fromStorage = null;
    try {
        const raw = sessionStorage.getItem('postLoginRedirect');
        fromStorage = resolveSafePostLoginPath(raw);
        if (fromStorage) sessionStorage.removeItem('postLoginRedirect');
    } catch {
        // ignore
    }
    const to = fromState || redirectParam || fromStorage || defaultPath;
    return <Navigate to={to} replace />;
}

function App() {
    const ctx = useContext(AuthContext);
    const navigate = useNavigate();
    setNavigate(navigate);
    const location = useLocation();
    recordPathname(location.pathname, location.search);
    const baseUserEvent = process.env.REACT_APP_EVENT_MANAGEMENT;

    const [isAuth, setIsAuth] = useState(undefined);

    useEffect(() => {
        const checkAuthentication = async () => {
            try {
                await ctx.amplifyAuth().currentAuthenticatedUser();
                setIsAuth(true); // User is authenticated
            } catch {
                setIsAuth(false); // User is not authenticated
            }
        };

        checkAuthentication();
    }, [setIsAuth, ctx]);

    useEffect(() => {
        if (isAuth === false && location?.pathname !== '/forgotPassword' && location?.pathname !== '/reset-password' && location?.pathname !== '/openknots/forgotPassword' && location?.pathname !== '/openknots/reset-password') {
            let logoutInProgress = false;
            try {
                logoutInProgress = sessionStorage.getItem('logoutInitiated') === '1';
            } catch {
                // ignore
            }

            if (location.pathname === '/login' || location.pathname === '/openknots/login') {
                return;
            }
            if (location.pathname === '/') {
                navigate(logoutInProgress ? getLogoutLoginPath() : '/login', { replace: true });
                return;
            }
            if (logoutInProgress) {
                navigate(getLogoutLoginPath(), { replace: true });
                return;
            }
            const target = buildUnauthenticatedLoginTarget(location.pathname, location.search, location.hash);
            if (target.loginPath === OPENKNOTS_LOGIN_PATH) {
                setLoginOrigin('openknots');
            }
            try {
                sessionStorage.setItem('postLoginRedirect', target.from);
            } catch {
                // ignore
            }
            navigate(target.to, {
                replace: true,
                state: { from: target.from },
            });
        }
    }, [isAuth, navigate, location.pathname, location.search, location.hash]);

    const handleActivityLog = useCallback(
        (userId) => {
            const payload = {
                payload: {
                    event: 'Logged in',
                    description: 'User logged in with MFA',
                },
                user_ulid: userId,
                type: 'account',
            };

            axios
                .post(`${baseUserEvent}/event`, payload)
                .then((response) => {
                    if (response.status === 200) {
                        //response
                    }
                })
                .catch((error) => {
                    console.log(error);
                });
        },
        [baseUserEvent],
    );

    useEffect(() => {
        if (isAuth) {
            handleActivityLog(ctx.userData?.user_ulid);
        }
    }, [ctx.userData?.user_ulid, isAuth, handleActivityLog]);

    useEffect(() => {
        // Request interceptor
        const requestInterceptor = axios.interceptors.request.use(function (config) {
            return new Promise((resolve, reject) => {
                ctx.amplifyAuth()
                    .currentSession()
                    .then((session) => {
                        const idTokenExpire = session.accessToken.getExpiration();
                        const currentTimeSeconds = Math.round(+new Date() / 1000);
                        if (idTokenExpire < currentTimeSeconds) {
                            ctx.onLogout();
                            reject(new Error('Token expired'));
                        } else {
                            config.headers.Authorization = 'Bearer ' + session.accessToken.getJwtToken();
                            resolve(config);
                        }
                    })
                    .catch(() => {
                        // No logged-in user: don't set auth header
                        resolve(config);
                    });
            });
        });

        // Response interceptor
        const responseInterceptor = axios.interceptors.response.use(
            (response) => {
                return response;
            },
            (error) => {
                if (
                    error.response &&
                    error.response.status === 401 &&
                    !error.config?.skipGlobalAuthLogout
                ) {
                    // Redirect to onLogout function from context
                    ctx.onLogout();
                }

                return Promise.reject(error);
            },
        );

        // Cleanup the interceptors when the component unmounts
        return () => {
            axios.interceptors.request.eject(requestInterceptor);
            axios.interceptors.response.eject(responseInterceptor);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const userRole = ctx?.userData?.user_organizations?.[0]?.user_org_role_maps?.[0]?.platform_role;
    const allowDemoRoutes = process.env.REACT_APP_SHOW_DASHBOARD_FIXED_ICONS === 'true';
    const showOpsSidebar = userRole === 'internal_ops' || userRole === 'admin';
    return (
        <div className={showOpsSidebar ? 'App ops-sidebar-visible' : 'App'}>
            {isAuth === undefined && <span>Loading...</span>}

            {isAuth && <Header />}
            <Suspense
                fallback={
                    <div className='mt-5 pt-5'>
                        <Loader />
                    </div>
                }>

                <Routes>
                    {/* Protected Routes */}
                    <Route>
                        <Route
                            element={
                                <Layout>
                                    <ProtectedLayoutOutlet isAuth={isAuth} />
                                </Layout>
                            }>
                            <Route path='/dashboard' element={<Dashboard />}>
                                <Route path='' element={<OpenKnotsContainer />} />
                            </Route>
                            <Route
                                path='/createRfq'
                                element={
                                    <ProductAccessGuard product='trade'>
                                        <CreateRfq />
                                    </ProductAccessGuard>
                                }
                            />
                            <Route
                                path='/priceSimulator'
                                element={
                                    <ProductAccessGuard product='trade'>
                                        <PriceSimulator />
                                    </ProductAccessGuard>
                                }
                            />
                            <Route path='/createProfile' element={<Profile />} />
                            <Route path='/manageProfiles' element={<ManageProfile />} />
                            <Route path='/rfqs/:id' element={<RfqDetails />} />
                            <Route
                                path='/transactions/:id'
                                element={
                                    <ProductAccessGuard product='any'>
                                        <TransactionDetails />
                                    </ProductAccessGuard>
                                }
                            />
                            <Route path='/chatEnquiry' element={<ChatEnquiry />} />
                            {/* Demo Routes */}
                            {allowDemoRoutes && (
                                <>
                                    <Route path='/opsTower' element={<OpsTowerDemo />} />
                                    <Route path='/dispute' element={<DisputeDemo />} />
                                    <Route path='/supplierDashboard' element={<SupplierDashboardDemo />} />
                                </>
                            )}

                            {/* Role-based Routes */}
                            {['buyer', 'supplier', 'broker'].includes(userRole) && (
                                <Route path='/myProfile' element={<MyProfile />} />
                            )}
                            {userRole === 'investor' && (
                                <>
                                    <Route path='/myProfile' element={<InvestorProfile />} />
                                    <Route path='/investmentHistory' element={<InvestmentHistory />} />
                                    <Route path='/liveFacilityLine' element={<LiveFacilityLine />} />
                                    <Route path='/liveFacilityLine/audit' element={<AuditTrail />} />
                                </>
                            )}
                            {['internal_ops', 'admin'].includes(userRole) && (
                                <>
                                    <Route path='/manageProfiles/:id' element={<Profile />} />
                                    <Route path='/requests' element={<InternalOpsRequestPage />} />
                                    <Route path='/facilities' element={<AllFacilities />} />
                                    <Route path='/facilities/:id' element={<FacilityDetails />} />
                                    <Route
                                        path='/create/masterInvestorFacility'
                                        element={<CreateMasterInvestorFacility />}
                                    />
                                    <Route path='/create/buyer-facility' element={<BuyerFacility />} />
                                    <Route path='/create/supplier-facility' element={<BuyerFacility />} />
                                    <Route path='/create/investor-facility' element={<InvestorFacility />} />
                                    <Route path='/pricingConfigurator' element={<PricingConfigurator />} />
                                    <Route path='/allBroker' element={<AllBroker />} />
                                    <Route path='/dashboard/allRfq' element={<AllRfq />} />
                                    <Route path='/dashboard/allTransaction' element={<AllTransaction />} />
                                    <Route path='/allFacilities' element={<AllFacilities />} />
                                    <Route path='/iam/:organizationUlid' element={<IAM />} />
                                </>
                            )}
                            {['buyer', 'supplier'].includes(userRole) && (
                                <Route path='/requests' element={<MyRequests />} />
                            )}
                            {userRole === 'supplier' && (
                                <Route path='/tradeSupportLimit' element={<TradeSupportLimitPage />} />
                            )}

                            {/* Common Routes */}
                            <Route path='/notifications' element={<Notifications />} />
                            <Route path='/userAssignment' element={<UserAssignment />} />
                            <Route
                                path='/requestFinancing'
                                element={
                                    <ProductAccessGuard product='deal'>
                                        <RequestFinancing />
                                    </ProductAccessGuard>
                                }
                            />
                            <Route path='/supplierDetails/:id' element={<SupplierDetails />} />
                            {/* OpenKnots paths handled in catch-all so same container stays mounted (no flicker) */}
                            <Route path='*' element={<OpenKnotsOrNotFound />} />
                        </Route>
                    </Route>

                    {/* Public Routes */}
                    <Route path='/' element={!isAuth ? <Login /> : <PostLoginRedirect />} />
                    <Route path='/login' element={!isAuth ? <Login /> : <PostLoginRedirect />} />
                    <Route
                        path='/openknots/login'
                        element={
                            !isAuth ? (
                                <OpenKnotsLogin />
                            ) : (
                                <PostLoginRedirect defaultPath='/dashboard?type=openKnots' />
                            )
                        }
                    />
                    <Route path='/forgotPassword' element={<ForgotPassword />} />
                    <Route path='/reset-password' element={<ResetPassword />} />
                    <Route path='openknots/forgotPassword' element={<OpenKnotsForgotPassword />} />
                    <Route path='/openknots/reset-password' element={<OpenKnotsResetPassword />} />
                </Routes>

                {/* Notifications & Other Components */}
                <ToastContainer
                    theme='dark'
                    draggable
                    autoClose={3000}
                    position='top-right'
                    hideProgressBar
                    newestOnTop
                />
                <ClearNotificationCatOnRouteChange />
            </Suspense>
        </div>
    );
}

// Wrap App in RealtimeProvider (authenticated WebSocket) so any child can subscribe.
// One shared socket per tab when realtime is enabled — OpenKnots must use host event relay.
export function AppWithWebSocket() {
    const ctx = useContext(AuthContext);
    const onSessionExpired = useCallback(() => {
        try {
            ctx?.onLogout?.();
        } catch {
            // ignore
        }
    }, [ctx]);

    return (
        <RealtimeProvider onSessionExpired={onSessionExpired}>
            <RealtimeStoreBinder />
            <App />
        </RealtimeProvider>
    );
}

// Default export used by index.js
export default AppWithWebSocket;
